import { getLatestRequiredVersion } from '../requests/studentRequests'

const LAST_ACTIVE_TIMESTAMP_KEY = 'lastActiveTimestamp'
const FOUR_HOURS_IN_MS = 4 * 60 * 60 * 1000
const VERSION_CHECK_TIMEOUT_MS = 2000 // 2 seconds - abort refresh if version check takes longer

export const saveLastActiveTimestamp = (): void => {
  try {
    localStorage.setItem(LAST_ACTIVE_TIMESTAMP_KEY, Date.now().toString())
  } catch (e) {
    console.error('Error saving last active timestamp:', e)
  }
}

export const checkVersionAndReloadIfStale = async (): Promise<void> => {
  try {
    const lastActiveTimestamp = localStorage.getItem(LAST_ACTIVE_TIMESTAMP_KEY)

    // If no timestamp, save current time and return
    if (!lastActiveTimestamp) {
      saveLastActiveTimestamp()
      return
    }

    const timeSinceLastActive = Date.now() - parseInt(lastActiveTimestamp, 10)

    // If less than 4 hours, update timestamp and return
    if (timeSinceLastActive < FOUR_HOURS_IN_MS) {
      console.log('less than 4 hours passed. no version check needed')
      saveLastActiveTimestamp()
      return
    }

    // User has been gone for 4+ hours, check version
    const currentBuildNumber = process.env.NEXT_PUBLIC_BUILD_NUMBER

    // Create a timeout promise that rejects after the specified timeout
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Version check timeout')), VERSION_CHECK_TIMEOUT_MS)
    })

    // Race the version check against the timeout
    const latestRequiredBuildNumber = await Promise.race([
      getLatestRequiredVersion(),
      timeoutPromise
    ])

    // Update timestamp either way
    saveLastActiveTimestamp()

    // If server requires a newer build, reload
    if (latestRequiredBuildNumber > currentBuildNumber) {
      console.log(`Version update required. Current: ${currentBuildNumber}, Required: ${latestRequiredBuildNumber}. Reloading...`)
      window.location.reload()
    } else {
      console.log('on latest version, no update required.')
    }
  } catch (e) {
    // Silently fail - don't disrupt user experience if version check fails
    console.error('Error checking version:', e)
    // Still update timestamp on error
    saveLastActiveTimestamp()
  }
}

export const setupVersionCheck = (): (() => void) => {
  // Save timestamp on initial load
  saveLastActiveTimestamp()

  // Check version when visibility changes from hidden to visible
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      checkVersionAndReloadIfStale()
    } else if (document.visibilityState === 'hidden') {
      saveLastActiveTimestamp()
    }
  }

  // Check version when window regains focus
  const handleFocus = () => {
    checkVersionAndReloadIfStale()
  }

  // Save timestamp when window loses focus
  const handleBlur = () => {
    saveLastActiveTimestamp()
  }

  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('focus', handleFocus)
  window.addEventListener('blur', handleBlur)

  // Return cleanup function
  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('focus', handleFocus)
    window.removeEventListener('blur', handleBlur)
  }
}
